<script setup lang="ts">
// The iTorgo logo badge, driven by Admin > Design > Logo: badge colour
// (logoBadgeColor, falling back to the brand colour) and shape
// (logoBadgeShape). The leopard is drawn in whichever of ink/white reads
// better on that colour. These settings used to be saved but ignored — every
// badge was hard-coded ink with a brand-coloured leopard.
import { computed } from 'vue'
import IlbirsIcon from '@/components/icons/IlbirsIcon.vue'
import { useThemeStore } from '@/stores/theme'

const props = defineProps<{
  // The surface the badge sits on. On the sun header a gold or yellow badge
  // would melt into the band, so it flips to ink with a sun-coloured leopard;
  // on a dark (ink) surface an ink badge flips to sun with an ink leopard.
  ground?: 'sun' | 'dark'
}>()

const themeStore = useThemeStore()

function lum(hex: string): number {
  const h = hex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return 0
  const ch = (i: number) => {
    const c = parseInt(h.substring(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4)
}
const ratio = (a: string, b: string) => {
  const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m)
  return (x + 0.05) / (y + 0.05)
}

const shapeOf = (s?: string) => s === 'circle' ? 'rounded-full' : s === 'square' ? 'rounded-md' : 'rounded-xl'

const badge = computed(() => {
  const t = themeStore.theme
  const ink = t.inkColor || '#14171F'
  const sun = t.sunColor || '#FFD43B'
  const bg = t.logoBadgeColor || t.primaryColor || '#F4B410'
  if (props.ground === 'sun' && t.logoBadgeShape !== 'transparent' && ratio(bg, sun) < 2) {
    return { style: { background: ink, color: sun }, shape: shapeOf(t.logoBadgeShape) }
  }
  if (props.ground === 'dark' && t.logoBadgeShape !== 'transparent' && ratio(bg, ink) < 2) {
    return { style: { background: sun, color: ink }, shape: shapeOf(t.logoBadgeShape) }
  }
  // A dark badge carries the sun-coloured leopard (when it reads), a light
  // badge the ink one.
  const darkBadge = ratio(bg, ink) < ratio(bg, '#FFFFFF')
  const fg = darkBadge ? (ratio(sun, bg) >= 4.5 ? sun : '#FFFFFF') : ink
  const shape = shapeOf(t.logoBadgeShape)
  const transparent = t.logoBadgeShape === 'transparent'
  return { style: transparent ? { color: ink } : { background: bg, color: fg }, shape }
})
</script>

<template>
  <span class="flex items-center justify-center shadow-sm" :class="badge.shape" :style="badge.style">
    <IlbirsIcon class="h-full w-full" />
  </span>
</template>
